import React, { useState, useEffect } from "react";
import orderService from "../../db/order-service";
import { localDB } from "../../db/pouchdb-config";
import "./AdminOrderList.scss";

const AdminOrderList = (props) => {
  const [orders, setOrders] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('ALL');
  const [viewMode, setViewMode] = useState('all'); // 'all', 'byProduct', 'byCustomer'
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [loading, setLoading] = useState(true);

  // Currency symbols by country
  const currencySymbols = {
    'US': '$',
    'IN': '₹',
    'GB': '£',
    'EU': '€',
    'JP': '¥'
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all orders
        const orderData = await orderService.getAllOrders();
        
        // Format orders for display
        const formattedOrders = orderData.map(order => ({
          orderId: order._id,
          customerId: order.customerId,
          customerName: order.customerName || "Customer",
          createdDate: new Date(order.date).toLocaleDateString(),
          totalPrice: order.totalAmount,
          items: order.items,
          status: order.status || 'pending',
          paymentStatus: order.paymentStatus || order.status || 'pending',
          paymentId: order.paymentId || 'N/A',
          paymentDate: order.updatedAt ? new Date(order.updatedAt).toLocaleString() : 'N/A',
          shippingAddress: order.shippingAddress,
          country: detectCountryFromAddress(order.shippingAddress)
        }));
        
        setOrders(formattedOrders);
        
        // Fetch products
        const productsResult = await localDB.find({
          selector: { type: 'product' }
        });
        setProducts(productsResult.docs);
        
        // Fetch customers
        const customersResult = await localDB.find({
          selector: { type: 'customer' }
        });
        setCustomers(customersResult.docs);
      } catch (err) {
        console.log("Couldn't retrieve data:", err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Simple function to detect country from address
  const detectCountryFromAddress = (address) => {
    if (!address) return 'US'; // Default
    
    const addressLower = address.toLowerCase();
    
    if (addressLower.includes('india') || addressLower.includes('mumbai') || 
        addressLower.includes('delhi') || addressLower.includes('bangalore')) {
      return 'IN';
    }
    
    if (addressLower.includes('uk') || addressLower.includes('united kingdom') || 
        addressLower.includes('london') || addressLower.includes('england')) {
      return 'GB';
    }
    
    if (addressLower.includes('europe') || addressLower.includes('germany') || 
        addressLower.includes('france') || addressLower.includes('spain')) {
      return 'EU';
    }
    
    if (addressLower.includes('japan') || addressLower.includes('tokyo')) {
      return 'JP';
    }
    
    return 'US'; // Default to US
  };

  // Format price based on country
  const formatPrice = (price, country) => {
    const symbol = currencySymbols[country] || '$';
    return `${symbol}${parseFloat(price).toFixed(2)}`;
  };

  const openOrderDetails = (order) => {
    props.handleOrderDetails(order);
  };

  const handleProductChange = async (e) => {
    const productId = e.target.value;
    setSelectedProduct(productId);
    
    if (productId) {
      setViewMode('byProduct');
      setLoading(true);
      try {
        // Get all orders that contain this product
        const allOrders = await orderService.getAllOrders();
        const ordersWithProduct = allOrders.filter(order => {
          if (!order.items || !Array.isArray(order.items)) return false;
          return order.items.some(item => item.productId === productId);
        });
        
        // Format orders
        const formattedOrders = ordersWithProduct.map(order => ({
          orderId: order._id,
          customerId: order.customerId,
          customerName: order.customerName || "Customer",
          createdDate: new Date(order.date).toLocaleDateString(),
          totalPrice: order.totalAmount,
          items: order.items.filter(item => item.productId === productId),
          status: order.status || 'pending',
          paymentStatus: order.paymentStatus || order.status || 'pending',
          paymentId: order.paymentId || 'N/A',
          paymentDate: order.updatedAt ? new Date(order.updatedAt).toLocaleString() : 'N/A',
          shippingAddress: order.shippingAddress,
          country: detectCountryFromAddress(order.shippingAddress)
        }));
        
        setOrders(formattedOrders);
      } catch (err) {
        console.error("Error fetching orders by product:", err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    } else {
      setViewMode('all');
      try {
        // Reload all orders
        const orderData = await orderService.getAllOrders();
        const formattedOrders = orderData.map(order => ({
          orderId: order._id,
          customerId: order.customerId,
          customerName: order.customerName || "Customer",
          createdDate: new Date(order.date).toLocaleDateString(),
          totalPrice: order.totalAmount,
          items: order.items,
          status: order.status || 'pending',
          paymentStatus: order.paymentStatus || order.status || 'pending',
          paymentId: order.paymentId || 'N/A',
          paymentDate: order.updatedAt ? new Date(order.updatedAt).toLocaleString() : 'N/A',
          shippingAddress: order.shippingAddress,
          country: detectCountryFromAddress(order.shippingAddress)
        }));
        setOrders(formattedOrders);
      } catch (err) {
        console.error("Error fetching all orders:", err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCustomerChange = async (e) => {
    const customerId = e.target.value;
    setSelectedCustomer(customerId);
    
    if (customerId) {
      setViewMode('byCustomer');
      setLoading(true);
      try {
        const customerOrders = await orderService.findOrdersByCustomerId(customerId);
        
        // Format orders
        const formattedOrders = customerOrders.map(order => ({
          orderId: order._id,
          customerId: order.customerId,
          customerName: order.customerName || "Customer",
          createdDate: new Date(order.date).toLocaleDateString(),
          totalPrice: order.totalAmount,
          items: order.items,
          status: order.status || 'pending',
          paymentStatus: order.paymentStatus || order.status || 'pending',
          paymentId: order.paymentId || 'N/A',
          paymentDate: order.updatedAt ? new Date(order.updatedAt).toLocaleString() : 'N/A',
          shippingAddress: order.shippingAddress,
          country: detectCountryFromAddress(order.shippingAddress)
        }));
        
        setOrders(formattedOrders);
      } catch (err) {
        console.error("Error fetching orders by customer:", err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    } else {
      setViewMode('all');
      try {
        // Reload all orders
        const orderData = await orderService.getAllOrders();
        const formattedOrders = orderData.map(order => ({
          orderId: order._id,
          customerId: order.customerId,
          customerName: order.customerName || "Customer",
          createdDate: new Date(order.date).toLocaleDateString(),
          totalPrice: order.totalAmount,
          items: order.items,
          status: order.status || 'pending',
          paymentStatus: order.paymentStatus || order.status || 'pending',
          paymentId: order.paymentId || 'N/A',
          paymentDate: order.updatedAt ? new Date(order.updatedAt).toLocaleString() : 'N/A',
          shippingAddress: order.shippingAddress,
          country: detectCountryFromAddress(order.shippingAddress)
        }));
        setOrders(formattedOrders);
      } catch (err) {
        console.error("Error fetching all orders:", err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    }
  };

  // Filter orders by country
  const filteredOrders = selectedCountry === 'ALL' 
    ? orders 
    : orders.filter(order => order.country === selectedCountry);

  if (loading) {
    return <div className="admin-order-list loading">Loading orders...</div>;
  }

  return (
    <div className="admin-order-list">
      <div className="order-header">
        <h1>Order Management</h1>
        
        <div className="filter-controls">
          <div className="filter-group">
            <label>View Orders:</label>
            <select 
              value={viewMode === 'byProduct' ? 'byProduct' : viewMode === 'byCustomer' ? 'byCustomer' : 'all'} 
              onChange={(e) => {
                const mode = e.target.value;
                setViewMode(mode);
                if (mode === 'all') {
                  setSelectedProduct('');
                  setSelectedCustomer('');
                }
              }}
            >
              <option value="all">All Orders</option>
              <option value="byProduct">By Product</option>
              <option value="byCustomer">By Customer</option>
            </select>
          </div>
          
          {viewMode === 'byProduct' && (
            <div className="filter-group">
              <label>Select Product:</label>
              <select 
                value={selectedProduct} 
                onChange={handleProductChange}
              >
                <option value="">-- Select Product --</option>
                {products.map(product => (
                  <option key={product._id} value={product._id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {viewMode === 'byCustomer' && (
            <div className="filter-group">
              <label>Select Customer:</label>
              <select 
                value={selectedCustomer} 
                onChange={handleCustomerChange}
              >
                <option value="">-- Select Customer --</option>
                {customers.map(customer => (
                  <option key={customer._id} value={customer._id}>
                    {customer.fname} {customer.lname} ({customer.email})
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div className="filter-group">
            <label>Filter by Country:</label>
            <select 
              value={selectedCountry} 
              onChange={(e) => setSelectedCountry(e.target.value)}
            >
              <option value="ALL">All Countries</option>
              <option value="US">United States</option>
              <option value="IN">India</option>
              <option value="GB">United Kingdom</option>
              <option value="EU">Europe</option>
              <option value="JP">Japan</option>
            </select>
          </div>
        </div>
      </div>
      
      {filteredOrders.length === 0 ? (
        <div className="no-orders">
          <p>No orders found for the selected criteria.</p>
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Order Date</th>
              <th>Items</th>
              <th>Total</th>
              <th>Status</th>
              <th>Payment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
              <tr key={order.orderId}>
                <td>{order.orderId.substring(0, 8)}...</td>
                <td>
                  <div>{order.customerName}</div>
                  <div className="customer-id">ID: {order.customerId}</div>
                </td>
                <td>{order.createdDate}</td>
                <td>
                  {order.items.slice(0, 2).map((item, idx) => (
                    <div key={idx} className="order-item">
                      {item.name} x {item.quantity}
                    </div>
                  ))}
                  {order.items.length > 2 && (
                    <div className="more-items">+{order.items.length - 2} more</div>
                  )}
                </td>
                <td>{formatPrice(order.totalPrice, order.country)}</td>
                <td>
                  <span className={`status-badge ${order.status}`}>
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                </td>
                <td>
                  <div className="payment-info">
                    <div className={`payment-status ${order.paymentStatus.toLowerCase()}`}>
                      {order.paymentStatus}
                    </div>
                    {order.paymentStatus === 'Paid' && (
                      <div className="payment-details">
                        <div>ID: {order.paymentId}</div>
                        <div>Date: {order.paymentDate}</div>
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <button onClick={() => openOrderDetails(order)}>View Details</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminOrderList;